import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
  Chip,
  Snackbar,
  CircularProgress
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import {
  ContentCopy,
  CheckCircle,
  Webhook,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useWebhookUrl } from '../../hooks/useWebhookUrl';

const SnackbarAlert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const WebhookManagement = () => {
  const {
    webhookUrl,
    isLoading,
    error,
    copyWebhookUrl,
  } = useWebhookUrl();

  const [showFullUrl, setShowFullUrl] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleCopy = async () => {
    const success = await copyWebhookUrl();
    setNotification({
      open: true,
      message: success ? 'Webhook URL copied to clipboard!' : 'Failed to copy webhook URL',
      severity: success ? 'success' : 'error'
    });
  };

  const handleToggleVisibility = () => {
    setShowFullUrl(!showFullUrl);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const maskUrl = (url) => {
    if (!url) return '';
    
    if (showFullUrl) return url;
    
    // Show the beginning and end, mask the clinic ID part
    const parts = url.split('/');
    if (parts.length > 3) {
      const masked = [...parts];
      // Mask the clinic_id (last part)
      if (masked[masked.length - 1]) {
        const clinicId = masked[masked.length - 1];
        // Show first 8 and last 8 characters, mask the middle
        if (clinicId.length > 16) {
          masked[masked.length - 1] = clinicId.substring(0, 8) + '•'.repeat(clinicId.length - 16) + clinicId.substring(clinicId.length - 8);
        } else {
          // For shorter IDs, show first 4 and last 4
          masked[masked.length - 1] = clinicId.substring(0, 4) + '•'.repeat(Math.max(0, clinicId.length - 8)) + clinicId.substring(Math.max(4, clinicId.length - 4));
        }
      }
      return masked.join('/');
    }
    return url;
  };

  const displayValue = () => {
    if (!webhookUrl) return '';
    return maskUrl(webhookUrl);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Webhook Configuration
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Copy this webhook URL and paste it into your Pabau CRM settings to enable automatic data synchronization. 
          This URL is unique to your clinic and allows Pabau to send real-time updates to MedInsight Pro.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Webhook Status
        </Typography>
        <Chip
          icon={webhookUrl ? <CheckCircle /> : undefined}
          label={webhookUrl ? "Ready for Configuration" : "Loading..."}
          color={webhookUrl ? "success" : "default"}
          variant={webhookUrl ? "filled" : "outlined"}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Webhook URL
        </Typography>
        <TextField
          fullWidth
          value={displayValue()}
          placeholder={isLoading ? "Loading webhook URL..." : "Webhook URL will appear here"}
          disabled={true}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Webhook color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {webhookUrl && (
                  <>
                    <IconButton
                      onClick={handleToggleVisibility}
                      disabled={isLoading}
                      size="small"
                    >
                      {showFullUrl ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    <IconButton
                      onClick={handleCopy}
                      disabled={isLoading}
                      size="small"
                      color="primary"
                    >
                      <ContentCopy />
                    </IconButton>
                  </>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Loading webhook URL...
          </Typography>
        </Box>
      )}

      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Important:</strong> Keep this webhook URL secure. Only configure it in your Pabau CRM settings. 
          Do not share this URL publicly as it provides direct access to update your clinic's data.
        </Typography>
      </Alert>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Setup Instructions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          1. Copy the webhook URL above by clicking the copy button
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          2. Log into your Pabau CRM admin panel
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          3. Navigate to Settings → Integrations → Webhooks
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          4. Add a new webhook endpoint and paste the URL
        </Typography>
        <Typography variant="body2" color="text.secondary">
          5. Enable the events you want to sync (appointments, clients, etc.)
        </Typography>
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarAlert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </SnackbarAlert>
      </Snackbar>
    </Paper>
  );
};

export default WebhookManagement; 